import { useEffect, useId, useRef, useState } from 'react';
import {
  LOCATION_TYPE_OPTIONS,
  PAYROLL_STATUS_OPTIONS,
  TAX_STATUS_OPTIONS,
  emptyFilters,
  hasActiveFilters,
  toggleAll,
  toggleValue,
  type FilterSelection,
  type LocationFilters,
} from './locationFilters';

interface DimensionProps<T extends string> {
  label: string;
  options: Array<{ value: T; label: string; chip?: string }>;
  selection: FilterSelection<T>;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onChange: (next: FilterSelection<T>) => void;
}

/** One filter dimension: a button (nothing chosen) or chips (something chosen), plus its checkbox menu. */
function FilterDimension<T extends string>({ label, options, selection, open, onOpenChange, onChange }: DimensionProps<T>) {
  const menuId = useId();
  const active = selection.all || selection.values.length > 0;
  const chipText = (value: T) => options.find((o) => o.value === value)?.chip ?? options.find((o) => o.value === value)?.label ?? value;

  return (
    <div className="filter-wrap">
      {active ? (
        <span className="filter-chips">
          {selection.all ? (
            <span className="filter-chip filter-chip-all">
              <button type="button" className="filter-chip-label" aria-haspopup="true" aria-expanded={open} aria-controls={menuId} onClick={() => onOpenChange(!open)}>
                All
              </button>
              <button type="button" className="filter-chip-x" aria-label={`Remove ${label} filter All`} onClick={() => onChange({ all: false, values: [] })}>
                <i className="fa fa-times-circle" aria-hidden="true" />
              </button>
            </span>
          ) : (
            selection.values.map((value) => (
              <span key={value} className="filter-chip">
                <button type="button" className="filter-chip-label" aria-haspopup="true" aria-expanded={open} aria-controls={menuId} onClick={() => onOpenChange(!open)}>
                  {chipText(value)}
                </button>
                <button type="button" className="filter-chip-x" aria-label={`Remove ${label} filter ${chipText(value)}`} onClick={() => onChange(toggleValue(selection, value))}>
                  <i className="fa fa-times-circle" aria-hidden="true" />
                </button>
              </span>
            ))
          )}
        </span>
      ) : (
        <button type="button" className="filter-btn" aria-haspopup="true" aria-expanded={open} aria-controls={menuId} onClick={() => onOpenChange(!open)}>
          {label} <i className="fa fa-angle-down" aria-hidden="true" />
        </button>
      )}
      {open && (
        <div className="filter-menu" id={menuId} role="group" aria-label={`${label} options`}>
          <label>
            <input type="checkbox" checked={selection.all} onChange={() => onChange(toggleAll(selection))} /> All
          </label>
          {options.map((option) => (
            <label key={option.value}>
              <input type="checkbox" checked={selection.values.includes(option.value)} onChange={() => onChange(toggleValue(selection, option.value))} /> {option.label}
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

interface LocationFilterBarProps {
  filters: LocationFilters;
  onChange: (filters: LocationFilters) => void;
  /** Hidden in View mode: the add button is the only thing on this row that changes data. */
  onAdd?: (() => void) | undefined;
}

type MenuKey = 'type' | 'payroll' | 'tax';

/**
 * Figma S01-S19: "Filters:" with Location Type / Payroll Status / Tax Registration Status dropdowns (each a checkbox menu with "All"), selected
 * values shown as chips, Clear All, a search box, and "+ Add New Location". Chip text follows the Figma ("Branch" for the Branches option).
 */
export function LocationFilterBar({ filters, onChange, onAdd }: LocationFilterBarProps) {
  const [openMenu, setOpenMenu] = useState<MenuKey | null>(null);
  const barRef = useRef<HTMLDivElement>(null);
  const searchId = useId();

  useEffect(() => {
    if (!openMenu) return undefined;
    const close = (event: MouseEvent) => {
      if (barRef.current && !barRef.current.contains(event.target as Node)) setOpenMenu(null);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpenMenu(null);
    };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', onKey);
    };
  }, [openMenu]);

  const anyActive = hasActiveFilters(filters);
  const toggleMenu = (key: MenuKey) => (open: boolean) => setOpenMenu(open ? key : null);

  return (
    <div className="filters-row" ref={barRef}>
      <div className="filters-left">
        <span className="filters-label">Filters:</span>
        <FilterDimension
          label="Location Type"
          options={LOCATION_TYPE_OPTIONS}
          selection={filters.type}
          open={openMenu === 'type'}
          onOpenChange={toggleMenu('type')}
          onChange={(type) => onChange({ ...filters, type })}
        />
        <FilterDimension
          label="Payroll Status"
          options={PAYROLL_STATUS_OPTIONS}
          selection={filters.payroll}
          open={openMenu === 'payroll'}
          onOpenChange={toggleMenu('payroll')}
          onChange={(payroll) => onChange({ ...filters, payroll })}
        />
        <FilterDimension
          label="Tax Registration Status"
          options={TAX_STATUS_OPTIONS}
          selection={filters.tax}
          open={openMenu === 'tax'}
          onOpenChange={toggleMenu('tax')}
          onChange={(tax) => onChange({ ...filters, tax })}
        />
        <button type="button" className={`clear-all${anyActive ? ' active' : ''}`} disabled={!anyActive} onClick={() => { onChange(emptyFilters()); setOpenMenu(null); }}>
          Clear All
        </button>
      </div>
      <div className="filters-right">
        <div className="search-wrap">
          <i className="fa fa-search search-icon" aria-hidden="true" />
          <label htmlFor={searchId} className="sr-only">Search branch name</label>
          <input
            id={searchId}
            className="search-input"
            type="search"
            placeholder="Search Branch Name"
            value={filters.search}
            onChange={(e) => onChange({ ...filters, search: e.target.value })}
          />
        </div>
        {onAdd && (
          <button type="button" className="add-location" onClick={onAdd}>
            <i className="fa fa-plus-circle bps-icon" aria-hidden="true" />
            Add New Location
          </button>
        )}
      </div>
    </div>
  );
}
